const MEDIA_PATH = '/api/v1/media/';
const MEDIA_FIELDS = new Set([
  'stream_url', 'snapshot_url', 'video_url', 'playback_url',
  'download_url', 'preview_url', 'file_url',
]);

export class MediaUrlError extends Error {
  constructor() {
    super('보호된 미디어 주소를 확인하지 못했습니다.');
    this.name = 'MediaUrlError';
  }
}

function unsafeCharacters(value: string): boolean {
  return value.includes('\\') || [...value].some((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
  });
}

function apiOrigin(apiUrl: string): URL {
  try {
    const api = new URL(apiUrl);
    if (!['https:', 'http:'].includes(api.protocol) || api.username || api.password) {
      throw new MediaUrlError();
    }
    return api;
  } catch {
    throw new MediaUrlError();
  }
}

export function normalizePrivateMediaUrl(reference: string | null | undefined, apiUrl: string): string | null {
  if (reference == null || reference === '') return null;
  try {
    const api = apiOrigin(apiUrl);
    if (reference !== reference.trim() || unsafeCharacters(reference)) throw new MediaUrlError();
    const relative = reference.startsWith('/') && !reference.startsWith('//');
    if (!relative && !/^https?:\/\//i.test(reference)) throw new MediaUrlError();
    const media = new URL(reference, api.origin);
    const historicalHttp = api.protocol === 'https:' && media.protocol === 'http:'
      && media.hostname === api.hostname && media.port === api.port;
    if ((!relative && media.origin !== api.origin && !historicalHttp)
      || media.username || media.password || media.hash || !media.pathname.startsWith(MEDIA_PATH)) {
      throw new MediaUrlError();
    }
    const decodedPath = decodeURIComponent(media.pathname);
    if (!decodedPath.startsWith(MEDIA_PATH) || unsafeCharacters(decodedPath)
      || decodedPath.split('/').some((part) => part === '.' || part === '..')) {
      throw new MediaUrlError();
    }
    return `${api.origin}${media.pathname}${media.search}`;
  } catch {
    throw new MediaUrlError();
  }
}

export function normalizePrivateMediaReferences<T>(data: T, apiUrl: string): T {
  const visit = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(visit);
    if (value === null || typeof value !== 'object') return value;
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key,
      MEDIA_FIELDS.has(key) && (typeof item === 'string' || item == null)
        ? normalizePrivateMediaUrl(item, apiUrl) : visit(item),
    ]));
  };
  return visit(data) as T;
}
